import { ParamMap } from '@angular/router';
import { SortDescriptor } from '@progress/kendo-data-query';
import { IFilter } from 'src/app/core/models/base';
import { parsePagingQuery, parseSortQuery } from 'src/app/shared/helpers/grid-query-params';
import { ISearchAssignmentsRequest, TaskStatues, UNASSIGNED_SENTINEL } from '../models/assignment';

export const ASSIGNMENTS_DEFAULT_SORT: SortDescriptor = { field: 'dueDate', dir: 'asc' };

export const ASSIGNMENTS_DEFAULT_STATUSES: TaskStatues[] = [
  TaskStatues.open,
  TaskStatues.inProgress,
  TaskStatues.review,
];

export const ASSIGNMENT_FILTER_KEYS = [
  'keyword',
  'contactId',
  'status',
  'hasNoSchedulerItems',
  'assignmentTypeIds',
  'executorIds',
] as const;

export const ASSIGNMENT_SORTABLE_FIELDS = [
  'title',
  'internalNumber',
  'dueDate',
  'createdOn',
  'client',
  'owner',
  'assignmentType',
  'taskStatus',
  'executionLocation',
];

/** True when the URL carries at least one filter param (paging/sort params do not count). */
export function hasAssignmentFilterParams(q: ParamMap): boolean {
  return ASSIGNMENT_FILTER_KEYS.some(key => q.has(key));
}

/** Maps a grid sort descriptor to the backend orderBy; unsorted falls back to the default. */
export function sortToOrderBy(sort: SortDescriptor[]): string[] {
  const s = sort?.[0];
  if (!s?.field || !s.dir) {
    return [ASSIGNMENTS_DEFAULT_SORT.field + ' ' + ASSIGNMENTS_DEFAULT_SORT.dir];
  }

  switch (s.field) {
    // TODO: QP-205 this only sorts Companies correctly
    case 'client':
    case 'executionLocation':
      return [s.field + '.companyName ' + s.dir];
    case 'owner':
      return ['owner.lastName ' + s.dir, 'owner.firstName ' + s.dir];
    default:
      return [s.field + ' ' + s.dir];
  }
}

/**
 * Builds the assignment list search request from the URL query params.
 * Used by both the resolver and the list component.
 */
export function buildAssignmentsSearchRequest(q: ParamMap): ISearchAssignmentsRequest {
  const paging = parsePagingQuery(q);
  const sort = parseSortQuery(q, ASSIGNMENTS_DEFAULT_SORT, ASSIGNMENT_SORTABLE_FIELDS);

  const request: ISearchAssignmentsRequest = {
    pageNumber: paging.pageNumber,
    pageSize: paging.pageSize,
    orderBy: sortToOrderBy(sort),
    taskStatuses: q.has('status')
      ? q.get('status')!.split(',').map(n => +n as TaskStatues)
      : [...ASSIGNMENTS_DEFAULT_STATUSES],
  };

  if (q.has('keyword')) request.keyword = q.get('keyword')!;
  if (q.has('hasNoSchedulerItems')) request.hasNoSchedulerItems = q.get('hasNoSchedulerItems') === '1';
  if (q.has('assignmentTypeIds')) request.assignmentTypeIds = q.get('assignmentTypeIds')!.split(',');
  if (q.has('contactId')) {
    request.advancedFilter = {
      logic: 'and',
      filters: [{ field: 'client.id', operator: 'eq', value: q.get('contactId') } as IFilter],
    };
  }
  if (q.has('executorIds')) {
    const all = q.get('executorIds')!.split(',');
    const real = all.filter(x => x !== UNASSIGNED_SENTINEL);
    if (real.length > 0) request.executorIds = real;
    if (all.includes(UNASSIGNED_SENTINEL)) request.includeUnassignedExecutors = true;
  }

  return request;
}
